import AppRoot from '@/app/components/AppRoot';
import SyncView from '@/app/pages/sync/SyncView';
import { createApp, h } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import { createStore } from 'vuex';
import { globalMethodsPlugin } from '@/app/globalMethods';
import { initResponseListener, sendBackendRequest } from '@/app/lib/Messaging/requests';
import FriendsViewAuthWrapper from '@/app/pages/friends/FriendsViewAuthWrapper';
import WhatsNewViewAuthWrapper from '@/app/pages/whats-new/WhatsNewViewAuthWrapper';
import MessageType from '@/wdm-common/Messaging/MessageType';
import RequestType from '@/wdm-common/Messaging/RequestType';
import { loadUmami, trackCurrentPage } from '@/wdm-common/Analytics/umami';
import { initTracking } from '@/app/lib/Analytics';

// Load Umami
loadUmami('59a10d16-b7a5-4a79-a394-ff0543c6dbdd', '', false)
    .then(() => {
        trackCurrentPage();
        initTracking();
    })
    .catch(() => {});

// Handle messages from extension content script. Convert them to events dispatched on document.
window.addEventListener(
    'message',
    (message) => {
        const event = new CustomEvent(
            message.data.type,
            {
                detail: message.data.data,
            }
        );
        document.dispatchEvent(event);
    },
    false
);

// Create Vuex store
const store = createStore({
    state: {
        /**
         * @type {string|boolean|null}
         */
        extensionVersion: null,

        /**
         * @type {object|boolean|null}
         */
        user: null,

        whatsNew: null,
    },
    mutations: {
        setExtensionVersion(state, value) {
            state.extensionVersion = value;
        },

        setUser(state, value) {
            state.user = value;
        },

        setWhatsNew(state, value) {
            state.whatsNew = value;
        },
    },
});

// Setup listening for request response messages.
initResponseListener();

// Extension has 3 seconds to load before events are fired to say it's not installed
const noExtensionTimeout = setTimeout(() => {
    document.dispatchEvent(new CustomEvent('WdmExtNotInstalled'));
    document.body.classList.add('no-ext');
}, 1000);

const setUser = (user) => {
    store.commit('setUser', user || false);

    for (const element of document.querySelectorAll('.fb-user-id-value')) {
        element.value = user ? user.userId : '';
    }
};

// Setup other extension request responses.
document.addEventListener(MessageType.DID_LOAD, (e) => {
    console.log('WdmExtDidLoad', e);

    clearTimeout(noExtensionTimeout);

    // Add the .has-ext class to the body.
    document.body.classList.add('has-ext');

    store.commit('setExtensionVersion', e.detail.version);

    // TODO: The following is still also done by jQuery in the legacy JS.

    // Put extension version into footer.
    if (document.getElementById('footer-ext-version')) {
        document.getElementById('footer-ext-version').innerHTML = '&nbsp; | &nbsp; Extension Version ' + e.detail.version;
    }

    // Put extension version into inputs that want it.
    // (e.g. contact form)
    for (const element of document.querySelectorAll('.ext-version-value')) {
        element.value = e.detail.version;
    }

    // Get the current user on startup.
    sendBackendRequest(RequestType.GET_USER)
        .then((response) => {
            setUser(response.user);
        })
        .catch(() => {
            // Probably no user yet. That's fine.
            setUser(false);
        });
});

document.addEventListener(MessageType.DID_SYNC_FRIENDS, (e) => {
    if (e.detail && e.detail.user) {
        store.commit('setUser', e.detail.user);
    }
});

document.addEventListener(MessageType.DID_LOAD_USER, (e) => {
    if (e.detail && e.detail.user) {
        store.commit('setUser', e.detail.user);
    }
});

// Create Vue app
const app = createApp({
    render: () => h(AppRoot),
});

// Create Vue router
const routes = [
    { path: '/sync', component: SyncView, name: 'sync' },
    { path: '/friends', component: WhatsNewViewAuthWrapper, name: 'whats-new' },
    { path: '/friends/:list/:page?', component: FriendsViewAuthWrapper, name: 'friends' },
];
const router = createRouter({
    history: createWebHistory(),
    routes,
});

app.use(router);
app.use(store);
app.use(globalMethodsPlugin);

app.mount('#vue-app-container');
